import {
  Action,
  ActionType,
  BillDateDelayFrom,
  CustomStepCategoryEnum,
  DiscountDuration,
  DiscountTypeEnum,
  ExecutionTypeEnum,
  FunnelInputTypeEnum,
  OfferIntents,
  OfferStages,
  OfferTypes,
  QuantityTypeEnum,
  RefundType,
  StepCategory,
  StepEmulatorDevices,
  StepFixedMediaCategory,
  StepMedia,
  StepMediaCategory,
  StepMediaSubCategory,
  StepResponseMedia,
  StepSectionWidths,
  StepStaticDataImageType,
} from '../../_models';
import { DomSanitizer } from '@angular/platform-browser';
import {
  descriptionExcludedStepTypes,
  getEmbeddedVideoUrl,
  isDirectVideoFile,
  StepCategoryOfferIntentsMapping,
  StepResponseContentDefaults,
} from './steps-helper';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { StepEmulatorService } from '../../_services';

export function initializeForm(
  formBuilder: FormBuilder,
  stepCategory: StepCategory,
  offerType: OfferTypes,
  editTemplate: boolean,
  offerIntent: OfferIntents = null,
  stepType: FunnelInputTypeEnum = null
): FormGroup {
  const form = formBuilder.group({
    id: null,
    subtitle: null,
    style: null,
    classes: [[]],
    is_popup: false,
    show_step_link: false,
    contact_type: null,
    enhanced_content: null,
    enhanced_mode: false,
    fail_if_any_input_invalid: false,
    invalid_message: null,
    hide_status: false,
    action_holdoffs: null,
    allow_undo: offerType !== OfferTypes.Downsell && offerType !== OfferTypes.Upsell,
    mobile_icon_button_type: null,
    use_bootstrap_spacing: true,
    enhanced_content_components: null,
    help: null,
    enabled: null,
    enter_product_funnel: false,
    enter_troubleshooter: false,
    error_lifeline: null,
    exit_lifeline: null,
    faq_topic: null,
    is_first_step: false,
    is_template: false,
    is_global: false,
    item_statuses: null,
    lifeline: null,
    lifeline_enabled: true,
    slug: null,
    sms_prompt: null,
    voice_file: null,
    voice_prompt: null,
    num_digits: null,
    offer_intent: OfferIntents.Legacy,
    phone_loop_count: 1,
    previous_inputs: [],
    group: null,
    forward_to_call_center: false,
    hide_if_invalid: true,
    campaign_products: [[]],
    product_funnels: [[]],
    matched_step_key: null,
    type: FunnelInputTypeEnum.Choice,
    category: [stepCategory],
    banner: [null],
    label: [null, Validators.required],
    name: [null, Validators.required],
    content: [null],
    button_styles: null,
    content_config: getContentWidths(formBuilder),
    show_selected_product: false,
  });

  const categoryToIntentMapping = {
    [CustomStepCategoryEnum.CancelOrderSurvey]: OfferIntents.CancelOrder,
    [CustomStepCategoryEnum.CancelSubSurvey]: OfferIntents.CancelSubscription,
    [CustomStepCategoryEnum.CancelTrialSurvey]: OfferIntents.CancelTrial,
    [CustomStepCategoryEnum.ReturnSurvey]: OfferIntents.ReturnOrder,
  };

  if (editTemplate) {
    if (stepCategory === CustomStepCategoryEnum.ResolveSurvey) {
      // one Resolve category maps to two intents (Resolve / ResolveSubscription) - keep the real one
      form.patchValue({
        offer_intent: offerIntent || OfferIntents.Resolve,
      });
    } else if (Object.keys(categoryToIntentMapping).includes(stepCategory.toString())) {
      form.patchValue({
        offer_intent: categoryToIntentMapping[stepCategory],
      });
    } else {
      form.patchValue({
        offer_intent: OfferIntents.Other,
      });
    }
  } else {
    form.patchValue({
      offer_intent: offerIntent || StepCategoryOfferIntentsMapping[stepCategory],
    });
  }

  switch (stepCategory) {
    case CustomStepCategoryEnum.Survey:
    case CustomStepCategoryEnum.ReturnSurvey:
    case CustomStepCategoryEnum.CancelSubSurvey:
    case CustomStepCategoryEnum.CancelOrderSurvey:
    case CustomStepCategoryEnum.CancelTrialSurvey:
    case CustomStepCategoryEnum.ResolveSurvey:
      form.patchValue({
        type: FunnelInputTypeEnum.Choice,
      });

      // We set the name based on the offer intent for survey steps & Hide the name input in UI
      form.patchValue({
        name: OfferStages[offerIntent],
      });

      form.setControl('inputs', formBuilder.array([], Validators.required));

      form.setControl(
        'button_styles',
        formBuilder.group({
          confirm: formBuilder.group({
            label: [null, Validators.required],
            style: [null],
            control_type: [null],
            style_type: [null],
          }),
        })
      );
      break;

    case CustomStepCategoryEnum.AdjustShippingFrequency:
    case CustomStepCategoryEnum.AdjustTrialFrequency:
    case CustomStepCategoryEnum.PauseSurvey:
      form.patchValue({
        type: FunnelInputTypeEnum.Radio,
      });

      form.setControl('inputs', formBuilder.array([], Validators.required));
      form.setControl('response_data', getResponseData(formBuilder, stepCategory));
      form.setControl(
        'button_styles',
        formBuilder.group({
          confirm: formBuilder.group({
            label: [null, Validators.required],
            style: [null],
            control_type: [null],
            style_type: [null],
          }),
        })
      );
      break;

    // Generic response step (INPUT_ACTION_STATUS) shares the Confirmation-response form shape:
    // a single INPUT_ACTION_STATUS input (the Continue button) plus the step label/content.
    case FunnelInputTypeEnum.InputActionStatus:
    case FunnelInputTypeEnum.Confirmation:
      let inputs;
      if (stepType === FunnelInputTypeEnum.Confirmation) {
        inputs = formBuilder.array([
          formBuilder.group({
            id: null,
            label: [null, Validators.required],
            actions: formBuilder.array([]),
            type: FunnelInputTypeEnum.Confirmation,
          }),
          formBuilder.group({
            id: null,
            label: [null, Validators.required],
            actions: formBuilder.array([]),
            type: FunnelInputTypeEnum.ConfirmationNo,
          }),
        ]);
      } else {
        inputs = formBuilder.array([
          formBuilder.group({
            id: null,
            label: [null, Validators.required],
            actions: formBuilder.array([]),
            type: FunnelInputTypeEnum.InputActionStatus,
          }),
        ]);
      }
      form.setControl('inputs', inputs);
      break;

    case FunnelInputTypeEnum.EnterTroubleshooter:
      form.patchValue({
        name: 'Troubleshooter',
        type: FunnelInputTypeEnum.EnterTroubleshooter,
      });
      form.get('label').clearValidators();
      form.get('label').updateValueAndValidity();
      form.setControl('inputs', getEnterTroubleshooterInputs(formBuilder));
      form.setControl('response_data', getResponseData(formBuilder));
      break;

    default:
      form.setControl('inputs', getDownsellInputs(formBuilder, stepCategory, offerType));
      form.setControl('response_data', getResponseData(formBuilder, stepCategory));
      break;
  }

  if (!editTemplate) {
    form.addControl('funnel', formBuilder.control(null));
  }

  return form;
}

function getContentWidths(formBuilder: FormBuilder) {
  const config = formBuilder.group({});

  stepWidthSections.forEach((section) => {
    const sectionGroup = formBuilder.group({
      is_hidden: [false],
      mobile: [100],
      tablet: [100],
      desktop: [100],
    });
    config.addControl(section, sectionGroup);
  });

  return config;
}

function getResponseContentWidths(formBuilder: FormBuilder) {
  const config = formBuilder.group({});

  responseWidthSections.forEach((section) => {
    const sectionGroup = formBuilder.group({
      is_hidden: [false],
      mobile: [100],
      tablet: [100],
      desktop: [100],
    });
    config.addControl(section, sectionGroup);
  });

  return config;
}

function getDefaultWidthConfig(sections: string[]) {
  return sections.reduce((acc, section) => {
    acc[section] = {
      isHidden: false,
      mobile: 100,
      tablet: 100,
      desktop: 100,
    };
    return acc;
  }, {});
}

export function getSurveyInputs(formBuilder: FormBuilder, offerType: OfferTypes): FormGroup {
  let actions: FormArray;
  if (offerType === OfferTypes.Survey) {
    actions = getSurveyActions(formBuilder);
  } else if (offerType === OfferTypes.SingleStep) {
    actions = getSingleStepSurveyActions(formBuilder);
  } else {
    actions = getDownsellSurveyActions(formBuilder);
  }

  const input = formBuilder.group({
    id: null,
    label: [null, Validators.required],
    help: null,
    next_step: null,
    type: FunnelInputTypeEnum.Radio,
    values: [[]],
    actions: actions,
    icon: null,
    hide_if_invalid: true,
    product_funnels: [[]],
    classes: [[]],
    is_alt_child_exit: false,
    matched_step_key: null,
    autopick_next_step: null,
    has_response_step: true,
    troubleshooter_enabled: false,
  });

  if (offerType === OfferTypes.Survey) {
    input.addControl('reason_category', new FormControl(null, Validators.required));
  }

  return input;
}

function getSurveyActions(formBuilder: FormBuilder): FormArray {
  return formBuilder.array([]);
}

function getDownsellSurveyActions(formBuilder: FormBuilder): FormArray {
  return formBuilder.array([
    formBuilder.group({
      id: null,
      execution_type: ExecutionTypeEnum.Immediate,
      resourcetype: ActionType.BillingCycle,
      billing_interval_days: null,
      check_last_discount: true,
      use_product_price: true,
      pause: false,
      final_billing_cycle_offset: 0,
      bill_now: false,
      bill_date_delay_from: BillDateDelayFrom.Today,
      next_bill_date_delay: [null, [Validators.required, Validators.min(1)]],
      max_bill_date_delay: null,
    }),
    formBuilder.group({
      id: null,
      resourcetype: ActionType.Save,
    }),
  ]);
}

function getSingleStepSurveyActions(formBuilder: FormBuilder): FormArray {
  return formBuilder.array([
    formBuilder.group({
      id: null,
      execution_type: ExecutionTypeEnum.Immediate,
      resourcetype: ActionType.BillingCycle,
      billing_interval_days: null,
      check_last_discount: true,
      use_product_price: true,
      pause: false,
      final_billing_cycle_offset: 0,
      bill_now: false,
      bill_date_delay_from: BillDateDelayFrom.Today,
      next_bill_date_delay: [null, [Validators.required, Validators.min(1)]],
      max_bill_date_delay: null,
    }),
  ]);
}

function getEnterTroubleshooterInputs(formBuilder: FormBuilder): FormArray {
  const baseFields = () => ({
    id: null,
    help: null,
    next_step: null,
    step: null,
    values: [[]],
    actions: formBuilder.array([]),
    icon: null,
    hide_if_invalid: true,
    product_funnels: [[]],
    classes: [[]],
    is_alt_child_exit: false,
    matched_step_key: null,
    autopick_next_step: null,
    builder_id: null,
    enter_product_funnel: false,
    enter_troubleshooter: false,
    item_statuses: null,
    button_styles: formBuilder.group({ style: null, control_type: null, style_type: null }),
  });

  return formBuilder.array([
    formBuilder.group({
      ...baseFields(),
      label: ['Troubleshooter Passed', Validators.required],
      type: FunnelInputTypeEnum.EnterTroubleshooter,
      has_response_step: true,
    }),
    formBuilder.group({
      ...baseFields(),
      label: ['Troubleshooter Failed', Validators.required],
      type: FunnelInputTypeEnum.AltChildExit,
      has_response_step: false,
    }),
    formBuilder.group({
      ...baseFields(),
      label: ['No Troubleshooter Found', Validators.required],
      type: FunnelInputTypeEnum.PathNotFound,
      has_response_step: false,
    }),
  ]);
}

function getDownsellInputs(formBuilder: FormBuilder, stepCategory: StepCategory, offerType: OfferTypes): FormArray {
  const eligibleForActions =
    offerType !== OfferTypes.SingleStep || stepCategory !== CustomStepCategoryEnum.OfferDetails;
  const inputs = [
    formBuilder.group({
      id: null,
      label: [null, Validators.required],
      help: null,
      next_step: null,
      step: null,
      type: FunnelInputTypeEnum.Choice,
      values: [[]],
      actions: eligibleForActions ? getDownsellInputActions(formBuilder, stepCategory, offerType) : [],
      icon: null,
      hide_if_invalid: true,
      product_funnels: [[]],
      classes: [[]],
      is_alt_child_exit: false,
      matched_step_key: null,
      autopick_next_step: null,
      has_response_step: true,
      builder_id: null,
      enter_product_funnel: false,
      enter_troubleshooter: false,
      item_statuses: null,
      button_styles: formBuilder.group({
        style: null,
        control_type: null,
        style_type: null,
      }),
    }),
    formBuilder.group({
      id: null,
      label: [null, Validators.required],
      help: null,
      next_step: null,
      step: null,
      type: FunnelInputTypeEnum.Choice,
      values: formBuilder.array([]),
      actions: formBuilder.array([]),
      icon: null,
      hide_if_invalid: true,
      product_funnels: [[]],
      classes: [[]],
      is_alt_child_exit: false,
      matched_step_key: null,
      autopick_next_step: null,
      has_response_step: false,
      builder_id: null,
      enter_product_funnel: false,
      enter_troubleshooter: false,
      item_statuses: null,
      button_styles: formBuilder.group({
        style: null,
        control_type: null,
        style_type: null,
      }),
    }),
  ];

  // If offerType is SingleStep, return only the first input
  if (offerType === OfferTypes.SingleStep) {
    return formBuilder.array([inputs[0]]);
  }

  return formBuilder.array(inputs);
}

function getDownsellInputActions(
  formBuilder: FormBuilder,
  stepCategory: StepCategory,
  offerType?: OfferTypes
): FormArray {
  let discount_index = 0,
    upsale_index = 0,
    exchangeable_index = null,
    action1: FormGroup,
    final_billing_cycle_offset = 0,
    discount = 20, // % or $
    next_bill_date_delay = 30, // days
    holdoff_delay = null;
  let actions = [];
  const action2 = formBuilder.group({
    id: null,
    resourcetype: ActionType.Save,
  });

  switch (stepCategory) {
    case CustomStepCategoryEnum.FixedAmountDownsell:
      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        resourcetype: ActionType.Discount,
        discount: [discount, [Validators.required, Validators.min(1)]],
        discount_type: [DiscountTypeEnum.Fixed, Validators.required],
        upsale_index: [null],
      });
      actions = [action1];
      break;

    case CustomStepCategoryEnum.PercentageDownsell:
      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        resourcetype: ActionType.Discount,
        discount: [discount, [Validators.required, Validators.min(1), Validators.max(100)]],
        discount_type: [DiscountTypeEnum.Percent, Validators.required],
        upsale_index: [null],
      });
      actions = [action1];
      break;

    case CustomStepCategoryEnum.ExtendTrial:
      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        resourcetype: ActionType.BillingCycle,
        billing_interval_days: null,
        check_last_discount: true,
        use_product_price: true,
        final_billing_cycle_offset,
        bill_now: false,
        next_bill_date_delay: [next_bill_date_delay, Validators.min(0)],
        max_bill_date_delay: null,
        holdoff_delay,
      });

      actions = [action1, action2];
      break;

    case CustomStepCategoryEnum.PauseSub:
      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        bill_date_delay_from: BillDateDelayFrom.Today,
        resourcetype: ActionType.BillingCycle,
        billing_interval_days: null,
        check_last_discount: true,
        use_product_price: true,
        pause: false,
        final_billing_cycle_offset,
        bill_now: false,
        next_bill_date_delay: [next_bill_date_delay, Validators.min(0)],
        max_bill_date_delay: null,
        holdoff_delay,
      });

      actions = [action1, action2];
      break;

    case CustomStepCategoryEnum.OfferDetails:
    case CustomStepCategoryEnum.LossAversion:
    case CustomStepCategoryEnum.Testimonial:
      action1 = formBuilder.group({
        id: null,
        resourcetype: ActionType.Save,
        execution_type: ExecutionTypeEnum.Immediate,
      });
      actions = [action1, action2];
      break;

    case CustomStepCategoryEnum.RefundOrder:
    case CustomStepCategoryEnum.DiscountFutureOrder:
    case CustomStepCategoryEnum.ReturnOrderRMA:
      const resourcetype =
        stepCategory === CustomStepCategoryEnum.RefundOrder || stepCategory === CustomStepCategoryEnum.ReturnOrderRMA
          ? ActionType.Refund
          : ActionType.Discount;

      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        resourcetype: resourcetype,
        discount: [discount, Validators.required],
        discount_index: [null],
        discount_type: [DiscountTypeEnum.Percent, Validators.required],
        ...(stepCategory === CustomStepCategoryEnum.RefundOrder
          ? { refund_type: [RefundType.NormalAfterCredit, Validators.required] }
          : {}),
        check_last_discount: true,
        use_product_price: true,
        bill_now: false,
        include_tax: true,
        holdoff_delay,
        final_billing_cycle_offset,
        upsale_index: [null],
        discount_duration: [DiscountDuration.Perpetual],
      });
      actions = [action1];
      break;

    case CustomStepCategoryEnum.ReviseOrder:
      action1 = formBuilder.group({
        id: null,
        resourcetype: ActionType.ChangeProduct,
        exchangeable_set_types: [[]],
        exchangeable_set: [[]],
        exchangeable_index,
        bill_now: false,
        subscription_only: false,
        quantity_type: QuantityTypeEnum.Any,
      });
      actions = [action1];
      break;

    case CustomStepCategoryEnum.UpsellOrder:
      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        resourcetype: ActionType.AddProduct,
        upsale_index,
        holdoff_delay,
      });
      actions = [action1];
      break;

    case CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount:
    case CustomStepCategoryEnum.ReActivateSubscription:
      const discountType =
        stepCategory === CustomStepCategoryEnum.ReActivateSubscription
          ? null
          : [DiscountTypeEnum.Percent, Validators.required];

      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        resourcetype: ActionType.Reactivate,
        discount: [discount, Validators.required],
        discount_index,
        discount_type: discountType,
        check_last_discount: false,
        use_product_price: true,
        bill_now: false,
        holdoff_delay,
        final_billing_cycle_offset,
        upsale_index: [null],
        discount_duration: [DiscountDuration.Perpetual],
        billing_interval_days: null,
        next_bill_date_delay: null,
        max_bill_date_delay: null,
      });
      actions = [action1];
      break;

    case CustomStepCategoryEnum.BillTrialNow:
      action1 = formBuilder.group({
        id: null,
        execution_type: ExecutionTypeEnum.Immediate,
        resourcetype: ActionType.BillingCycle,
        billing_interval_days: null,
        check_last_discount: true,
        use_product_price: true,
        final_billing_cycle_offset,
        bill_now: true,
        next_bill_date_delay: null,
        max_bill_date_delay: 0,
        holdoff_delay,
      });
      actions = [action1];
      break;
  }

  // SaveAction is not allowed on single-step funnels, so drop it there (the real action stays).
  if (offerType === OfferTypes.SingleStep) {
    actions = actions.filter((action) => action.get('resourcetype').value !== ActionType.Save);
  }

  return formBuilder.array(actions);
}

function getResponseData(formBuilder: FormBuilder, stepCategory: StepCategory = null): FormGroup {
  const label = 'continue';
  const responseDefault = stepCategory !== null ? StepResponseContentDefaults[stepCategory] : null;

  return formBuilder.group({
    label: [responseDefault ? responseDefault.label : null, Validators.required],
    help: null,
    name: [null],
    type: FunnelInputTypeEnum.InputActionStatus,
    category: FunnelInputTypeEnum.InputActionStatus,
    banner: null,
    subtitle: null,
    content: [responseDefault ? responseDefault.content : null, Validators.required],
    offer_intent: OfferIntents.Other,
    style: null,
    classes: [[]],
    is_popup: false,
    allow_undo: false,
    show_step_link: false,
    contact_type: null,
    enhanced_content: null,
    enhanced_mode: false,
    fail_if_any_input_invalid: false,
    invalid_message: null,
    hide_status: false,
    action_holdoffs: null,
    mobile_icon_button_type: null,
    use_bootstrap_spacing: true,
    enhanced_content_components: null,
    inputs: formBuilder.array([
      {
        label,
        help: null,
        next_step: null,
        type: FunnelInputTypeEnum.InputActionStatus,
        values: [],
        actions: [],
        icon: null,
        hide_if_invalid: true,
        product_funnels: [],
        classes: [],
        is_alt_child_exit: false,
        matched_step_key: null,
        autopick_next_step: null,
      },
    ]),
    content_config: getResponseContentWidths(formBuilder),
  });
}

export function patchInputs(
  form: FormGroup,
  formBuilder: FormBuilder,
  data: any,
  useExternalButtons: boolean,
  stepMedia: StepMedia,
  stepResponseMedia: StepResponseMedia,
  sanitizer: DomSanitizer,
  stepEmulatorService: StepEmulatorService,
  offerType?: OfferTypes
) {
  if (useExternalButtons) {
    let inputs;
    if (
      data.category === CustomStepCategoryEnum.AdjustShippingFrequency ||
      data.category === CustomStepCategoryEnum.AdjustTrialFrequency ||
      data.category === CustomStepCategoryEnum.PauseSurvey
    ) {
      inputs = data.inputs.slice(0, -1).map(() => {
        return getSurveyInputs(formBuilder, data.offer_type);
      });
      inputs.push(
        formBuilder.group({
          id: null,
          label: [null, Validators.required],
          help: null,
          next_step: null,
          step: null,
          type: FunnelInputTypeEnum.Choice,
          values: formBuilder.array([]),
          actions: formBuilder.array([]),
          icon: null,
          hide_if_invalid: true,
          product_funnels: [[]],
          classes: [[]],
          is_alt_child_exit: false,
          matched_step_key: null,
          autopick_next_step: null,
          has_response_step: false,
          troubleshooter_enabled: false,
          builder_id: null,
          enter_product_funnel: false,
          enter_troubleshooter: false,
          item_statuses: null,
          button_styles: formBuilder.group({
            style: null,
            control_type: null,
            style_type: null,
          }),
        })
      );
    } else {
      inputs = data.inputs.map(() => {
        return getSurveyInputs(formBuilder, data.offer_type);
      });
    }

    if (inputs) {
      form.setControl('inputs', formBuilder.array(inputs, Validators.required));
    }
  }

  if (data.content_config === null) {
    data.content_config = getDefaultWidthConfig(stepWidthSections);
  }

  if (data.response_data && data.response_data.content_config === null) {
    data.response_data.content_config = getDefaultWidthConfig(responseWidthSections);
  }

  if (data && data.inputs && data.inputs.length) {
    data.inputs.forEach((inputData) => {
      if (inputData && inputData.button_styles === null) {
        inputData.button_styles = {
          style: null,
          control_type: null,
          style_type: null,
        };
      }
    });
  }

  if (data.button_styles === null) {
    data.button_styles = {
      style: null,
      control_type: null,
      style_type: null,
    };
  }

  if (!data.response_data) {
    data.response_data = getResponseData(formBuilder);
  }

  if (offerType === OfferTypes.SingleStep) {
    const controlsToUpdate = [form.get('content'), form.get('response_data.content'), form.get('response_data.label')];

    // Remove required validators for SingleStep offer type
    controlsToUpdate.forEach((control) => {
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });

    if (descriptionExcludedStepTypes.includes(data.category)) {
      data.content = null;
    }

    // Set default "Confirm" text for AdjustShippingFrequency step type
    if (
      data.category === CustomStepCategoryEnum.AdjustShippingFrequency ||
      data.category === CustomStepCategoryEnum.AdjustTrialFrequency
    ) {
      if (!data.button_styles.confirm) {
        data.button_styles.confirm = {
          label: null,
          style: null,
          control_type: null,
          style_type: null,
        };
      }
      if (!data.button_styles.confirm.label || data.button_styles.confirm.label.trim() === '') {
        data.button_styles.confirm.label = 'Confirm';
      }

      if (data.inputs && data.inputs.length > 0 && data.inputs[0]) {
        if (!data.inputs[0].label || data.inputs[0].label.trim() === '') {
          data.inputs[0].label = 'Confirm';
        }
      }
    }
  }

  const formInputsArray = form.get('inputs') as FormArray;
  data.inputs.forEach((dataInput, index) => {
    const formInput = formInputsArray.at(index) as FormGroup;
    if (formInput) {
      const formActions = formInput.get('actions') as FormArray;

      dataInput.actions.forEach((action: Action, actionIndex: number) => {
        if (actionIndex >= formActions.length) {
          formActions.push(new FormControl(action));
        }
      });
    }
  });

  form.patchValue(data);

  if (data.static_data) {
    stepMedia = updateMedia(stepMedia, data.static_data, sanitizer);
    stepEmulatorService.updateStepMedia(stepMedia);
  }

  if (data && data.response_data && data.response_data.static_data) {
    stepResponseMedia = updateMedia(stepResponseMedia, data.response_data.static_data, sanitizer);
    stepEmulatorService.updateResponseMedia(stepResponseMedia);
  }
}

export function updateMedia(media, static_data: any, sanitizer: DomSanitizer) {
  const { image, image_mobile, image_tablet, image_type, embed_code } = static_data;

  switch (image_type) {
    case StepStaticDataImageType.Fixed:
      const setFixedImage = (device: StepEmulatorDevices, image: any) => {
        if (image) {
          media.category = StepMediaCategory.Fixed;
          media.fixedImage.category = StepFixedMediaCategory.Upload;
          media.fixedImage[device] = image;
        }
      };

      setFixedImage(StepEmulatorDevices.Mobile, image_mobile);
      setFixedImage(StepEmulatorDevices.Tablet, image_tablet);
      setFixedImage(StepEmulatorDevices.Desktop, image);
      break;

    case StepStaticDataImageType.Gallery:
      if (image) {
        media.category = StepMediaCategory.Fixed;
        media.fixedImage.category = StepFixedMediaCategory.Gallery;
        media.fixedImage.productImage = image;
      }
      break;

    case StepStaticDataImageType.Product:
      media.category = StepMediaCategory.Product;
      media.subCategory = StepMediaSubCategory.Product;
      break;

    case StepStaticDataImageType.Tagged:
      media.category = StepMediaCategory.Product;
      media.subCategory = StepMediaSubCategory.Tagged;
      break;

    case StepStaticDataImageType.Embed:
      media.category = StepMediaCategory.Embed;
      media.embedVideo.url = embed_code;
      media.embedVideo.sanitizedURL = getEmbeddedVideoUrl(media.embedVideo.url, sanitizer);
      media.embedVideo.isURLValid = !!media.embedVideo.sanitizedURL;
      media.embedVideo.isFile = isDirectVideoFile(media.embedVideo.url);
      break;

    case StepStaticDataImageType.None:
      media.category = StepMediaCategory.NoMedia;
      break;
  }
  return media;
}

export function externalButtonUsage(stepCategory: StepCategory): boolean {
  const specialStepCategories: StepCategory[] = [
    CustomStepCategoryEnum.Survey,
    CustomStepCategoryEnum.ReturnSurvey,
    CustomStepCategoryEnum.CancelSubSurvey,
    CustomStepCategoryEnum.CancelOrderSurvey,
    CustomStepCategoryEnum.CancelTrialSurvey,
    CustomStepCategoryEnum.ResolveSurvey,
    CustomStepCategoryEnum.AdjustShippingFrequency,
    CustomStepCategoryEnum.AdjustTrialFrequency,
    CustomStepCategoryEnum.PauseSurvey,
  ];

  return specialStepCategories.includes(stepCategory);
}

const stepWidthSections = [
  StepSectionWidths.Banner,
  StepSectionWidths.Label,
  StepSectionWidths.Subtitle,
  StepSectionWidths.Content,
  StepSectionWidths.AcceptButtonContent,
  StepSectionWidths.RejectButtonContent,
  StepSectionWidths.StaticImage,
];

const responseWidthSections = [StepSectionWidths.Label, StepSectionWidths.Content, StepSectionWidths.StaticImage];
